import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface IOrbitRailLoadMoreOptions {
  /** Rows loaded so far. */
  total: number;
  /** Rows drawn at first, and added by each See more. */
  batch: number;
  /** The server holds rows beyond `total`. */
  hasMore?: boolean;
  loadingMore?: boolean;
  failed?: boolean;
  onLoadMore?: () => void;
  onRetry?: () => void;
}

export interface IOrbitRailLoadMore {
  shown: number;
  /** Rows remain, loaded or on the server: the control is drawn. */
  canShowMore: boolean;
  loading: boolean;
  /** The last request for more failed; the control retries it. */
  failed: boolean;
  showMore: () => void;
  /** What the last See more did, for the live region. */
  announcement: { added: number; shown: number } | null;
  /** Index of the first row the last See more added. */
  firstNewIndex: number | null;
  firstNewRowRef: React.RefObject<HTMLButtonElement>;
}

interface IPendingBatch {
  from: number;
  /** `total` when the outstanding page was requested. */
  total: number;
  fetching: boolean;
  sawLoading: boolean;
}

/**
 * Incremental See more over a rail list. Rows already loaded are revealed first; when they run out
 * and the server has more, pages are requested until the batch is full, the server is exhausted,
 * or a page lands without adding a row (the next one could do the same).
 */
const useOrbitRailLoadMore = ({
  total,
  batch,
  hasMore = false,
  loadingMore = false,
  failed = false,
  onLoadMore,
  onRetry
}: IOrbitRailLoadMoreOptions): IOrbitRailLoadMore => {
  const [target, setTarget] = useState(batch);
  const [announcement, setAnnouncement] = useState<IOrbitRailLoadMore['announcement']>(null);
  const [firstNewIndex, setFirstNewIndex] = useState<number | null>(null);
  const pendingRef = useRef<IPendingBatch | null>(null);
  const firstNewRowRef = useRef<HTMLButtonElement>(null);

  const shown = Math.min(target, total);
  const canShowMore = total > shown || hasMore;
  const requested = target > batch;
  const showFailure = failed && requested;

  const showMore = useCallback(() => {
    if (loadingMore) {
      return;
    }

    let fetch: (() => void) | undefined;

    if (showFailure) {
      fetch = onRetry;
    } else {
      const next = shown + batch;
      setTarget(next);

      if (total < next && hasMore) {
        fetch = onLoadMore;
      }
    }

    pendingRef.current = { from: shown, total, fetching: fetch !== undefined, sawLoading: false };
    setFirstNewIndex(shown);
    fetch?.();
  }, [loadingMore, showFailure, shown, batch, total, hasMore, onLoadMore, onRetry]);

  useEffect(() => {
    const pending = pendingRef.current;

    if (pending === null) {
      return;
    }

    if (pending.fetching) {
      if (loadingMore) {
        pending.sawLoading = true;
        return;
      }

      const progressed = total > pending.total;

      // The request has not started yet: nothing landed, nothing failed, no loading seen.
      if (!failed && !progressed && hasMore && !pending.sawLoading) {
        return;
      }

      if (!failed && progressed && hasMore && shown < target) {
        pending.total = total;
        pending.sawLoading = false;
        onLoadMore?.();
        return;
      }
    }

    pendingRef.current = null;

    if (failed) {
      return;
    }

    setAnnouncement({ added: shown - pending.from, shown });

    // Only when the control took focus with it: a user who moved on while a page loaded stays put.
    const focusLost = document.activeElement === null || document.activeElement === document.body;

    if (!canShowMore && focusLost) {
      firstNewRowRef.current?.focus();
    }
  }, [loadingMore, failed, total, hasMore, shown, target, canShowMore, onLoadMore]);

  return {
    shown,
    canShowMore,
    loading: loadingMore,
    failed: showFailure,
    showMore,
    announcement,
    firstNewIndex,
    firstNewRowRef
  };
};

export default useOrbitRailLoadMore;
